import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Snowflake, Leaf, Scissors, Settings, ShieldCheck, Map } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { getProductBySlug } from '../data/products';
export function Home() {
  // Exactly the 6 featured products specified
  const featuredSlugs = ['yarbo-lawn-mower', 'yarbo-lawn-mower-pro', 'yarbo-snow-blower', 'yarbo-blower', 'yarbo-trimmer-module', 'yarbo-all-season-pro-bundle'];
  const featuredProducts = featuredSlugs.map(slug => getProductBySlug(slug)).filter((p): p is NonNullable<typeof p> => Boolean(p));
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-100px'
    },
    transition: {
      duration: 0.6
    }
  };
  const seasonCards = [{
    title: 'Spring/Summer Lawn Care',
    desc: 'Wire-free robotic mowing for Michigan yards up to 6 acres.',
    link: '/category/lawn-mowers',
    accent: 'bg-yarbo-green',
    image: 'https://www.yarbo.com/cdn/shop/files/Frame_2036457935.png?v=1776754454&width=1280'
  }, {
    title: 'Winter Snow Removal',
    desc: '2-stage autonomous snow clearing for driveways and walkways.',
    link: '/category/snow-blowers',
    accent: 'bg-yarbo-blue',
    image: 'https://www.yarbo.com/cdn/shop/files/Frame_2036457870_4b952e93-1c66-490b-a235-0b842bff8939.png?v=1776674606&width=1280'
  }, {
    title: 'Fall Leaf Cleanup',
    desc: 'High-force blowing for wooded properties and large lots.',
    link: '/category/leaf-blowers',
    accent: 'bg-orange-600',
    image: 'https://www.yarbo.com/cdn/shop/files/Frame_2036458140.png?v=1777195453&width=1280'
  }, {
    title: 'Edge & Detail Trimming',
    desc: 'Hands-free trimming module for clean lawn edges.',
    link: '/category/modules-accessories',
    accent: 'bg-yarbo-charcoal',
    image: 'https://www.yarbo.com/cdn/shop/files/Frame_2036457909.png?v=1777186797&width=1280'
  }];
  return <div className="w-full" data-id="element-305">
      {/* Hero Section */}
      <section className="relative bg-yarbo-charcoal overflow-hidden" data-id="element-306">
        {/* Subtle background lineup image */}
        <div className="absolute inset-0 z-0" data-id="element-307">
          <img src="https://www.yarbo.com/cdn/shop/files/10002_1944x_6d8d2d36-95dc-4371-9f64-784332369683.webp?v=1772938423&width=3860" alt="" className="w-full h-full object-cover opacity-40 lg:opacity-60" data-id="element-308" />
          <div className="absolute inset-0 bg-gradient-to-r from-yarbo-charcoal via-yarbo-charcoal/85 lg:via-yarbo-charcoal/70 to-yarbo-charcoal/20" data-id="element-309"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-20 pb-24 lg:pt-32 lg:pb-40" data-id="element-310">
          <div className="max-w-2xl" data-id="element-311">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7
          }} data-id="element-312">
              <span className="inline-block py-1.5 px-3 rounded-full bg-yarbo-yellow/15 text-yarbo-yellow text-xs font-bold tracking-wider uppercase mb-6 border border-yarbo-yellow/30" data-id="element-313">
                Michigan Yarbo Sales & Setup Guidance
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6" data-id="element-314">
                Yarbo Robotic Yard Care for Michigan Homes
              </h1>
              <p className="text-lg md:text-xl text-gray-300 mb-10 leading-relaxed" data-id="element-315">
                Automate mowing, snow clearing, leaf cleanup, and trimming with
                one modular robotic system — built for Michigan lawns,
                driveways, and large rural properties.
              </p>

              <div className="flex flex-wrap gap-4" data-id="element-316">
                <Link to="/shop" className="btn-primary text-base px-7 py-4" data-id="element-317">
                  Shop Yarbo Products
                </Link>
                <Link to="/contact" className="btn-secondary bg-white text-yarbo-dark border-white hover:bg-gray-100 text-base px-7 py-4" data-id="element-318">
                  Request a Quote
                </Link>
                <Link to="/installation-demos" className="btn-secondary bg-transparent text-white border-gray-500 hover:bg-white/10 hover:border-gray-300 text-base px-7 py-4" data-id="element-319">
                  Schedule a Demo
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust / Quick Benefits Strip */}
      <section className="bg-white border-b border-gray-200 py-8 relative z-20 -mt-8 mx-4 sm:mx-8 lg:mx-auto max-w-7xl rounded-xl shadow-lg" data-id="element-320">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 px-6" data-id="element-321">
          {[{
          icon: Scissors,
          label: 'Mowing'
        }, {
          icon: Snowflake,
          label: 'Snow Removal'
        }, {
          icon: Leaf,
          label: 'Leaf Cleanup'
        }, {
          icon: Settings,
          label: 'Trimming'
        }, {
          icon: ShieldCheck,
          label: 'Modular Y Series'
        }, {
          icon: Map,
          label: 'Michigan Setup Help'
        }].map((benefit, idx) => <div key={idx} className="flex flex-col items-center text-center p-2" data-id="element-322">
              <benefit.icon className="w-8 h-8 text-yarbo-yellow mb-2" data-id="element-323" />
              <span className="text-sm font-bold text-yarbo-dark" data-id="element-324">
                {benefit.label}
              </span>
            </div>)}
        </div>
      </section>

      {/* One Core. Multiple Yard Jobs. */}
      <section className="py-20 bg-gray-50" data-id="element-325">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-326">
          <motion.div {...fadeIn} className="text-center max-w-3xl mx-auto mb-16" data-id="element-327">
            <h2 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-6" data-id="element-328">
              One Core. Multiple Yard Jobs.
            </h2>
            <p className="text-lg text-gray-600" data-id="element-329">
              The Yarbo Y Series is built around a universal Core. Instead of
              buying a separate machine for every season, you attach the module
              you need for the job at hand.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-330">
            <motion.div {...fadeIn} className="order-2 lg:order-1" data-id="element-331">
              <div className="space-y-8" data-id="element-332">
                <div className="flex" data-id="element-333">
                  <div className="flex-shrink-0 mt-1" data-id="element-334">
                    <div className="w-10 h-10 rounded-full bg-yarbo-dark text-white flex items-center justify-center font-bold text-lg" data-id="element-335">
                      1
                    </div>
                  </div>
                  <div className="ml-4" data-id="element-336">
                    <h3 className="text-xl font-bold text-yarbo-dark mb-2" data-id="element-337">
                      Start with the Yarbo Core
                    </h3>
                    <p className="text-gray-600" data-id="element-338">
                      The intelligent base unit houses the battery, navigation
                      system, and drive tracks built to handle Michigan terrain
                      and steep slopes.
                    </p>
                  </div>
                </div>
                <div className="flex" data-id="element-339">
                  <div className="flex-shrink-0 mt-1" data-id="element-340">
                    <div className="w-10 h-10 rounded-full bg-yarbo-yellow text-yarbo-dark flex items-center justify-center font-bold text-lg" data-id="element-341">
                      2
                    </div>
                  </div>
                  <div className="ml-4" data-id="element-342">
                    <h3 className="text-xl font-bold text-yarbo-dark mb-2" data-id="element-343">
                      Add Your Modules
                    </h3>
                    <p className="text-gray-600" data-id="element-344">
                      Attach the Lawn Mower, Snow Blower, Leaf Blower, or
                      Trimmer module based on the season or task. Modules are
                      sold separately or as part of a complete system.
                    </p>
                  </div>
                </div>
                <div className="flex" data-id="element-345">
                  <div className="flex-shrink-0 mt-1" data-id="element-346">
                    <div className="w-10 h-10 rounded-full bg-yarbo-green text-white flex items-center justify-center font-bold text-lg" data-id="element-347">
                      3
                    </div>
                  </div>
                  <div className="ml-4" data-id="element-348">
                    <h3 className="text-xl font-bold text-yarbo-dark mb-2" data-id="element-349">
                      Map, Schedule & Relax
                    </h3>
                    <p className="text-gray-600" data-id="element-350">
                      Use the Yarbo app to set virtual boundaries without
                      perimeter wires, schedule zones, and monitor progress from
                      your phone.
                    </p>
                  </div>
                </div>
              </div>

              {/* Module chips */}
              <div className="mt-8 flex flex-wrap gap-2" data-id="element-351">
                {['Yarbo Core', 'Mower Module', 'Snow Blower Module', 'Blower Module', 'Trimmer Module'].map((chip, idx) => <span key={chip} className={`text-xs font-bold px-3 py-1.5 rounded-full ${idx === 0 ? 'bg-yarbo-dark text-white' : 'bg-white border border-gray-200 text-gray-700'}`} data-id="element-352">
                    {chip}
                  </span>)}
              </div>

              <div className="mt-10" data-id="element-353">
                <Link to="/category/bundles" className="btn-primary" data-id="element-354">
                  Shop All-Season Bundles{' '}
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-355" />
                </Link>
              </div>
            </motion.div>

            <motion.div {...fadeIn} className="order-1 lg:order-2 bg-white p-8 rounded-2xl shadow-xl border border-gray-100" data-id="element-356">
              <div className="aspect-square bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl overflow-hidden flex items-center justify-center p-6" data-id="element-357">
                <img src="https://www.yarbo.com/cdn/shop/files/Yarbo_4-in-1_Yard_Robot_with_Lawn_Mower_Pro_Module.png?v=1777093281&width=1445" alt="Yarbo modular yard robot system with Core and modules" className="w-full h-full object-contain" data-id="element-358" />
              </div>
              <p className="text-xs text-gray-500 text-center mt-4 italic" data-id="element-359">
                Yarbo Core shown with Mower Pro, Snow Blower, and Blower
                modules. Configurations vary by product.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Choose Your Michigan Season */}
      <section className="py-20 bg-white" data-id="element-360">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-361">
          <motion.div {...fadeIn} className="text-center mb-16" data-id="element-362">
            <h2 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-4" data-id="element-363">
              Choose Your Michigan Season
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-364">
              From lake-effect snow to fast-growing spring grass, equip your
              property for the yard work you most want to automate.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-365">
            {seasonCards.map((season, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: idx * 0.08
          }} whileHover={{
            y: -8
          }} className="group relative rounded-2xl overflow-hidden shadow-lg h-80 bg-yarbo-charcoal" data-id="element-366">
                <img src={season.image} alt={season.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-367" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent" data-id="element-368"></div>
                <div className="absolute inset-0 p-6 flex flex-col justify-end" data-id="element-369">
                  <div className={`${season.accent} w-12 h-1 mb-4 rounded-full`} data-id="element-370"></div>
                  <h3 className="text-xl font-bold text-white mb-2" data-id="element-371">
                    {season.title}
                  </h3>
                  <p className="text-gray-200 text-sm mb-4" data-id="element-372">{season.desc}</p>
                  <Link to={season.link} className="text-yarbo-yellow font-bold text-sm flex items-center group-hover:underline" data-id="element-373">
                    Explore <ArrowRight className="ml-1 w-4 h-4" data-id="element-374" />
                  </Link>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-gray-50" data-id="element-375">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-376">
          <div className="flex flex-col md:flex-row justify-between md:items-end mb-12 gap-4" data-id="element-377">
            <motion.div {...fadeIn} className="max-w-2xl" data-id="element-378">
              <h2 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-4" data-id="element-379">
                Featured Yarbo Systems
              </h2>
              <p className="text-lg text-gray-600" data-id="element-380">
                Popular configurations for Michigan homeowners — from
                single-season tools to the all-season bundle.
              </p>
            </motion.div>
            <Link to="/shop" className="hidden md:flex items-center text-yarbo-dark font-bold hover:text-yarbo-yellow transition-colors whitespace-nowrap" data-id="element-381">
              View All Products <ArrowRight className="ml-2 w-5 h-5" data-id="element-382" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-383">
            {featuredProducts.map((product, idx) => <motion.div key={product.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: idx * 0.08
          }} className="h-full" data-id="element-384">
                <ProductCard product={product} data-id="element-385" />
              </motion.div>)}
          </div>

          <div className="mt-10 text-center md:hidden" data-id="element-386">
            <Link to="/shop" className="btn-secondary w-full" data-id="element-387">
              View All Products
            </Link>
          </div>
        </div>
      </section>

      {/* Product Finder / Chooser */}
      <section className="py-24 bg-yarbo-charcoal text-white relative overflow-hidden" data-id="element-388">
        <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-yarbo-yellow/10 rounded-full blur-3xl" data-id="element-389"></div>
        <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-96 h-96 bg-yarbo-blue/10 rounded-full blur-3xl" data-id="element-390"></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-391">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-6 text-white" data-id="element-392">
            Not Sure Which Yarbo Fits Your Property?
          </h2>
          <p className="text-xl text-gray-300 mb-12" data-id="element-393">
            Tell us a bit about your Michigan yard, and we'll recommend the
            right setup.
          </p>

          <div className="bg-white/10 backdrop-blur-md border border-white/20 p-8 rounded-2xl text-left" data-id="element-394">
            <div className="space-y-6" data-id="element-395">
              <div data-id="element-396">
                <label className="block text-sm font-bold text-gray-200 mb-3" data-id="element-397">
                  1. What do you want automated?
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-398">
                  {['Lawn Mowing', 'Snow Removal', 'Leaf Cleanup', 'All-Season'].map(opt => <button key={opt} className="px-4 py-3 rounded-lg border border-gray-600 hover:border-yarbo-yellow hover:bg-yarbo-yellow/10 transition-colors text-sm font-medium text-center text-white" data-id="element-399">
                      {opt}
                    </button>)}
                </div>
              </div>

              <div data-id="element-400">
                <label className="block text-sm font-bold text-gray-200 mb-3" data-id="element-401">
                  2. How large is the property?
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-402">
                  {['Under 1 Acre', '1-3 Acres', '3+ Acres / Steep Slopes'].map(opt => <button key={opt} className="px-4 py-3 rounded-lg border border-gray-600 hover:border-yarbo-yellow hover:bg-yarbo-yellow/10 transition-colors text-sm font-medium text-center text-white" data-id="element-403">
                        {opt}
                      </button>)}
                </div>
              </div>

              <div data-id="element-404">
                <label className="block text-sm font-bold text-gray-200 mb-3" data-id="element-405">
                  3. Do you already own a Yarbo Core?
                </label>
                <div className="grid grid-cols-2 gap-3" data-id="element-406">
                  {['Yes — need modules', 'No — need complete system'].map(opt => <button key={opt} className="px-4 py-3 rounded-lg border border-gray-600 hover:border-yarbo-yellow hover:bg-yarbo-yellow/10 transition-colors text-sm font-medium text-center text-white" data-id="element-407">
                        {opt}
                      </button>)}
                </div>
              </div>

              <div className="pt-6 border-t border-white/10 text-center" data-id="element-408">
                <Link to="/contact" className="btn-primary w-full md:w-auto px-10 py-4 text-lg" data-id="element-409">
                  Get a Recommendation
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Michigan SEO Content */}
      <section className="py-20 bg-white" data-id="element-410">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-411">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-412">
            <motion.div {...fadeIn} data-id="element-413">
              <span className="inline-block text-yarbo-yellow text-xs font-bold tracking-wider uppercase mb-4" data-id="element-414">
                Built For Local Conditions
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-6" data-id="element-415">
                Built for Michigan's Demanding Seasons
              </h2>
              <div className="space-y-5 text-gray-600 leading-relaxed" data-id="element-416">
                <p data-id="element-417">
                  Michigan properties bring real challenges — lake-effect
                  snowfalls, fast spring grass, wooded lots full of fall leaves,
                  and sprawling rural or lakefront acreage. Traditional yard
                  equipment means hours of weekend work every season.
                </p>
                <p data-id="element-418">
                  <strong className="text-yarbo-dark" data-id="element-419">Robotic Mowing:</strong>{' '}
                  The Yarbo Lawn Mower and Lawn Mower Pro handle up to 6 acres
                  with no buried perimeter wires. RTK GPS navigation works
                  through Michigan winters without breaking with frost heaves.
                </p>
                <p data-id="element-420">
                  <strong className="text-yarbo-dark" data-id="element-421">
                    Robotic Snow Removal:
                  </strong>{' '}
                  The Yarbo Snow Blower module uses a true 2-stage clearing
                  mechanism for heavy, wet Midwest snow. It clears driveways and
                  walkways and returns to its dock to recharge automatically.
                </p>
                <p data-id="element-422">
                  <strong className="text-yarbo-dark" data-id="element-423">
                    Long Driveways & Wooded Lots:
                  </strong>{' '}
                  Whether you're managing a half-mile gravel driveway or a
                  wooded lakefront lot, Yarbo's modular system lets you add the
                  right tools for the job.
                </p>
                <p data-id="element-424">
                  As a local retailer,{' '}
                  <strong className="text-yarbo-dark" data-id="element-425">Merem Robotics</strong>{' '}
                  helps Michigan homeowners pick the right configuration, plan
                  docking and antenna placement, and ask the right questions
                  before they buy.
                </p>
              </div>
              <div className="mt-8" data-id="element-426">
                <Link to="/guides" className="text-yarbo-dark font-bold hover:text-yarbo-yellow flex items-center" data-id="element-427">
                  Read Our Michigan Buying Guides{' '}
                  <ArrowRight className="ml-2 w-4 h-4" data-id="element-428" />
                </Link>
              </div>
            </motion.div>
            <motion.div {...fadeIn} className="grid grid-cols-2 gap-4" data-id="element-429">
              <div className="rounded-xl overflow-hidden bg-gray-100 h-64" data-id="element-430">
                <img src="https://www.yarbo.com/cdn/shop/files/Frame_2036457960.png?v=1776774554&width=1280" alt="Yarbo on a large Michigan lawn" className="w-full h-full object-cover" data-id="element-431" />
              </div>
              <div className="rounded-xl overflow-hidden bg-gray-100 h-64 mt-8" data-id="element-432">
                <img src="https://www.yarbo.com/cdn/shop/files/Frame_2036457839_3.png?v=1776673568&width=1280" alt="Yarbo clearing snow on a Michigan driveway" className="w-full h-full object-cover" data-id="element-433" />
              </div>
              <div className="rounded-xl overflow-hidden bg-gray-100 h-64 -mt-4 col-span-2" data-id="element-434">
                <img src="https://www.yarbo.com/cdn/shop/files/20260430-102551.png?v=1777515965&width=1445" alt="Yarbo on a large rural Michigan property" className="w-full h-full object-cover" data-id="element-435" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-20 bg-gray-50" data-id="element-436">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-437">
          <div className="text-center mb-12" data-id="element-438">
            <h2 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-4" data-id="element-439">
              Compare Core Systems
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-440">
              Find the right configuration for your needs at a glance.
            </p>
          </div>

          {/* Desktop table */}
          <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-441">
            <table className="w-full text-left border-collapse" data-id="element-442">
              <thead data-id="element-443">
                <tr className="bg-gray-50 border-b border-gray-200" data-id="element-444">
                  <th className="p-5 font-bold text-gray-700 w-1/5" data-id="element-445">Feature</th>
                  <th className="p-5 font-bold text-yarbo-dark text-center" data-id="element-446">
                    Lawn Mower
                  </th>
                  <th className="p-5 font-bold text-yarbo-dark text-center" data-id="element-447">
                    Lawn Mower Pro
                  </th>
                  <th className="p-5 font-bold text-yarbo-dark text-center" data-id="element-448">
                    Snow Blower
                  </th>
                  <th className="p-5 font-bold text-yarbo-dark text-center" data-id="element-449">
                    Blower
                  </th>
                  <th className="p-5 font-bold text-yarbo-dark text-center bg-yarbo-yellow/10" data-id="element-450">
                    All-Season Bundle
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-451">
                {[{
                label: 'Best for mowing',
                vals: ['✓ Standard', '✓ Heavy / Steep', '—', '—', '✓ Pro']
              }, {
                label: 'Best for snow',
                vals: ['—', '—', '✓ 12 in / 24 in width', '—', '✓ Included']
              }, {
                label: 'Best for leaves',
                vals: ['—', '—', '—', '✓ 21 N force', '✓ Included']
              }, {
                label: 'Handles 35° slopes',
                vals: ['✓', '✓', '✓', '✓', '✓']
              }, {
                label: 'Requires Yarbo Core?',
                vals: ['Included', 'Included', 'Included', 'Included', 'Included']
              }, {
                label: 'Module-only option?',
                vals: ['Available', 'Available', 'Available', 'Available', '—']
              }].map((row, idx) => <tr key={idx} data-id="element-452">
                    <td className="p-5 text-gray-700 font-medium" data-id="element-453">
                      {row.label}
                    </td>
                    {row.vals.map((val, i) => <td key={i} className={`p-5 text-center text-sm ${i === 4 ? 'bg-yarbo-yellow/5 font-medium' : 'text-gray-600'}`} data-id="element-454">
                        {val}
                      </td>)}
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* Mobile stacked cards */}
          <div className="lg:hidden space-y-4" data-id="element-455">
            {[{
            name: 'Yarbo Lawn Mower',
            best: 'Standard lawn mowing up to 6 acres',
            slopes: '35° / 70%',
            core: 'Included',
            moduleOnly: 'Available',
            accent: 'border-gray-200'
          }, {
            name: 'Yarbo Lawn Mower Pro',
            best: 'Heavy or steep lawn conditions',
            slopes: '35° / 70%',
            core: 'Included',
            moduleOnly: 'Available',
            accent: 'border-gray-200'
          }, {
            name: 'Yarbo Snow Blower',
            best: 'Up to 12 in snow, 24 in clearing width',
            slopes: 'Driveways & walkways',
            core: 'Included',
            moduleOnly: 'Available',
            accent: 'border-gray-200'
          }, {
            name: 'Yarbo Blower',
            best: 'Leaves and debris (21 N force)',
            slopes: '35° / 70%',
            core: 'Included',
            moduleOnly: 'Available',
            accent: 'border-gray-200'
          }, {
            name: 'All-Season Bundle',
            best: 'Year-round mowing + snow + leaves',
            slopes: '35° / 70%',
            core: 'Included',
            moduleOnly: '—',
            accent: 'border-yarbo-yellow bg-yarbo-yellow/5'
          }].map((sys, idx) => <div key={idx} className={`bg-white rounded-xl border p-5 ${sys.accent}`} data-id="element-456">
                <h3 className="font-bold text-yarbo-dark text-lg mb-3" data-id="element-457">
                  {sys.name}
                </h3>
                <dl className="space-y-2 text-sm" data-id="element-458">
                  <div className="flex justify-between gap-4" data-id="element-459">
                    <dt className="text-gray-500" data-id="element-460">Best for</dt>
                    <dd className="text-gray-800 font-medium text-right" data-id="element-461">
                      {sys.best}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4" data-id="element-462">
                    <dt className="text-gray-500" data-id="element-463">Terrain</dt>
                    <dd className="text-gray-800 font-medium text-right" data-id="element-464">
                      {sys.slopes}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4" data-id="element-465">
                    <dt className="text-gray-500" data-id="element-466">Yarbo Core</dt>
                    <dd className="text-gray-800 font-medium text-right" data-id="element-467">
                      {sys.core}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4" data-id="element-468">
                    <dt className="text-gray-500" data-id="element-469">Module-only?</dt>
                    <dd className="text-gray-800 font-medium text-right" data-id="element-470">
                      {sys.moduleOnly}
                    </dd>
                  </div>
                </dl>
              </div>)}
          </div>

          <div className="text-center mt-10" data-id="element-471">
            <Link to="/shop" className="btn-secondary" data-id="element-472">
              View All Products
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-yarbo-yellow text-yarbo-dark text-center px-4" data-id="element-473">
        <div className="max-w-3xl mx-auto" data-id="element-474">
          <h2 className="text-3xl md:text-5xl font-extrabold mb-6" data-id="element-475">
            Ready to Bring Yarbo to Your Michigan Property?
          </h2>
          <p className="text-xl mb-10 opacity-90" data-id="element-476">
            Get local support, expert advice, and the right robotic yard care
            system from Merem Robotics.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-477">
            <Link to="/contact" className="btn-dark text-lg px-8 py-4" data-id="element-478">
              Request a Quote
            </Link>
            <Link to="/installation-demos" className="btn-secondary bg-white hover:bg-gray-50 text-lg px-8 py-4 border-none" data-id="element-479">
              Schedule a Demo
            </Link>
            <Link to="/shop" className="btn-secondary bg-transparent border-yarbo-dark hover:bg-yarbo-dark/10 text-lg px-8 py-4" data-id="element-480">
              Shop Products
            </Link>
          </div>
        </div>
      </section>
    </div>;
}